import React from 'react';
import { motion } from 'framer-motion';
import { BrainCircuit, UserX, VolumeX } from 'lucide-react';
const painCards = [{
  icon: <BrainCircuit className="w-7 h-7 text-accent-yellow" data-id="element-105" />,
  title: 'The Translation Trap',
  body: 'You think in your language, translate in your head, and by the time you speak — the moment is gone.'
}, {
  icon: <UserX className="w-7 h-7 text-accent-yellow" data-id="element-106" />,
  title: 'The Identity Death',
  body: 'Back home you\'re sharp, respected, charismatic. Here? You\'re just "the quiet one with the accent."'
}, {
  icon: <VolumeX className="w-7 h-7 text-accent-yellow" data-id="element-107" />,
  title: 'The English Freeze',
  body: "Someone says something fast. Your brain locks. You smile and nod — but inside, you're panicking."
}];
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 40
  },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      delay: i * 0.12,
      ease: [0.22, 1, 0.36, 1]
    }
  })
};
export function PainSection() {
  return <section className="py-24 bg-surface relative" data-id="element-108">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-109">
        <div className="space-y-12" data-id="element-110">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.6
        }} className="border-l-4 border-accent-orange pl-6 py-2" data-id="element-111">
            <h2 className="font-heading text-4xl md:text-6xl text-primary" data-id="element-112">
              IT HAPPENED AGAIN, DIDN'T IT?
            </h2>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-80px'
        }} transition={{
          duration: 0.7
        }} className="space-y-8 text-lg md:text-xl text-muted leading-relaxed" data-id="element-113">
            <p data-id="element-114">
              You were standing in a circle of people. Someone told a joke.
              Everyone laughed. You smiled and nodded—but inside, you were{' '}
              <span className="text-primary font-semibold" data-id="element-115">
                frantically trying to translate
              </span>{' '}
              what just happened.
            </p>
            <p data-id="element-116">
              You had something smart to say. Something funny. But by the time
              you built the sentence in your head, the conversation had already
              moved on.
            </p>
            <div className="bg-background p-8 rounded-2xl border border-border my-8" data-id="element-117">
              <p className="text-primary italic font-medium text-center text-xl md:text-2xl" data-id="element-118">
                "You feel like a 'limited' version of yourself. Back home,
                you're sharp, respected, and charismatic. Here? You're just the
                'quiet person with the accent'."
              </p>
            </div>
            <p data-id="element-119">
              It's frustrating. It's exhausting. And worst of all, it makes you
              feel invisible.
            </p>
            <p className="text-2xl md:text-3xl font-heading text-primary pt-4" data-id="element-120">
              It's not your English that's broken. <br data-id="element-121" />
              <span className="text-accent-yellow" data-id="element-122">It's your strategy.</span>
            </p>
          </motion.div>

          {/* Pain Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-4" data-id="element-123">
            {painCards.map((card, i) => <motion.div key={i} custom={i} variants={fadeUp} initial="hidden" whileInView="show" viewport={{
            once: true,
            margin: '-60px'
          }} className="bg-background border border-border border-l-4 border-l-accent-yellow rounded-xl p-6" data-id="element-124">
                <div className="mb-4" data-id="element-125">{card.icon}</div>
                <h3 className="font-heading text-xl text-primary mb-2" data-id="element-126">
                  {card.title}
                </h3>
                <p className="text-muted text-sm leading-relaxed" data-id="element-127">
                  {card.body}
                </p>
              </motion.div>)}
          </div>
        </div>
      </div>
    </section>;
}